import { useEffect, useRef, useState, type FocusEvent, type KeyboardEvent } from 'react';
import { usePresence } from '../../hooks/usePresence';

/**
 * A toolbar menu's opening and closing: a click elsewhere, Escape, or tabbing out closes it, and
 * Escape puts focus back on its button. `enabled` false keeps it shut, as while a tool works.
 * Render the panel while it's `present`: once shut, it's `leaving` for a moment, on its way out.
 */
export function usePopover(enabled = true) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const shown = open && enabled;
  const { present, leaving } = usePresence(shown);

  useEffect(() => {
    if (!shown) return;
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && rootRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Captured first, so Escape closes the menu and nothing else.
      event.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [shown]);

  return {
    open,
    shown,
    present,
    leaving,
    setOpen,
    rootRef,
    triggerRef,
    /**
     * Tabbing out of the menu closes it. Focus going nowhere, as on a click on the menu's own
     * text or the window losing focus, doesn't: a click elsewhere closes it already.
     */
    onBlur: (event: FocusEvent<HTMLElement>) => {
      const next = event.relatedTarget;
      if (next instanceof Node && !event.currentTarget.contains(next)) setOpen(false);
    },
  };
}

/** Whether there's a mouse or trackpad, and so likely a keyboard to type a search with. */
export const finePointer = () =>
  typeof window.matchMedia === 'undefined' ? false : window.matchMedia('(pointer: fine)').matches;

/**
 * Puts focus in a menu's search as it opens, so typing finds a choice straight away; on a touch
 * screen, that would only pop the keyboard up.
 */
export function useSearchFocus(shown: boolean) {
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (shown && finePointer()) searchRef.current?.focus();
  }, [shown]);
  return searchRef;
}

/** Up and Down move between a menu's search and its choices, round and round. */
export function moveWithArrows(event: KeyboardEvent<HTMLElement>) {
  if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
  const stops = [...event.currentTarget.querySelectorAll<HTMLElement>('.menu-stop')];
  const active = document.activeElement;
  // A language's pin is beside its choice; the arrows continue from that same row.
  const choice = active?.closest('.menu-choice')?.querySelector<HTMLElement>('.menu-stop');
  const at = stops.findIndex((stop) => stop === active || stop === choice);
  const step = event.key === 'ArrowDown' ? 1 : -1;
  event.preventDefault();
  stops[(at + step + stops.length) % stops.length]?.focus();
}
